import { Button } from '@app/app/_components/Button/Button';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { LoginWidget } from '@app/app/_widgets/Login/LoginWidget';
import { SelectClientWidget } from '@app/app/_widgets/SelectClient/SelectClientWidget';
import { SaveProgressModal } from '@app/app/esales/life/_sections/steps/SaveProgressModal';
import { Client } from '@app/domain/model/Client';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseLifeInsuranceResult } from '@app/service/eshop/life/actions/purchaseLifeInsurance';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { LoginModalSection } from '@app/app/auth/login/components/LoginModalSection';
import { useLogin } from '@app/app/auth/login/hooks/useLogin';
import { TenantMetadata } from '@app/infrastructure/tenant/model/TenantMetadata';
import { pushToDataLayer } from '@app/infrastructure/gtm/pushToDataLayer';

interface Props {
    isModalOpen: boolean;
    closeModal: () => void;
    handleSaveSuccess: () => void;
    handleSubmit: () => void;
    formState: FormState<PurchaseLifeInsuranceResult>;
    openModal: () => void;
    authState: AuthState;
    clients: Client[];
    onAuthSuccess?: () => void;
    tenant: TenantMetadata;
}

export function HalfwayOverviewFooter({
    isModalOpen,
    closeModal,
    handleSaveSuccess,
    handleSubmit,
    formState,
    openModal,
    authState,
    clients,
    onAuthSuccess,
    tenant,
}: Props) {
    const t = useTranslations();

    const [isLoginOpen, setIsLoginOpen] = useState(false);
    const [isSelectClientOpen, setIsSelectClientOpen] = useState(false);

    const handleLoginOpen = (shouldOpen: boolean) => {
        setIsLoginOpen(shouldOpen);
    };

    const handleSelectClientOpen = (shouldOpen: boolean) => {
        setIsSelectClientOpen(shouldOpen);
    };

    const login = useLogin(authState, handleSubmit, handleLoginOpen, handleSelectClientOpen, onAuthSuccess);

    const handleLoginButtonClick = () => {
        pushToDataLayer('login_attempt', {
            flow_name: 'life_insurance',
            step_number: 2,
            sub_step_number: 2.5,
        });
        login.handleLoginClick();
    };

    if (!formState.data?.formData || !formState.data?.packages) {
        return null;
    }

    return (
        <>
            <SaveProgressModal
                open={isModalOpen}
                onClose={closeModal}
                onSuccess={handleSaveSuccess}
                data={formState.data.formData}
                packages={formState.data.packages}
            />

            <div className="flex w-full flex-col-reverse gap-4 sm:flex-row">
                <Button
                    preventDefault
                    className="w-full"
                    variant="outlined"
                    onClick={openModal}
                    rightAddon={<ArrowRightIcon className="size-4" />}>
                    {t('eshop.life.saveAndContinueLater')}
                </Button>
                <Button
                    className="w-full"
                    onClick={handleLoginButtonClick}
                    preventDefault
                    rightAddon={<ArrowRightIcon className="size-4" />}>
                    {authState.authenticated && authState.clientSelected ? t('general.next') : t('auth.login')}
                </Button>
            </div>

            <LoginModalSection
                isLoginOpen={isLoginOpen}
                isSelectClientOpen={isSelectClientOpen}
                handleLoginClose={login.handleLoginClose}
                handleSelectClientClose={login.handleSelectClientClose}
                loginWidget={
                    <LoginWidget disableTwoFactorAuth onSuccess={login.handleLoginSuccess} noBankAuth tenant={tenant} />
                }
                selectClientWidget={
                    <SelectClientWidget clients={clients} onSuccess={login.handleSelectClientSuccess} />
                }
            />
        </>
    );
}
